// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Bot, DollarSign, List, Package } from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/Accordion';
import ErrorBoundary from 'components/ErrorBoundary';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { isWrittenRulesAgentEnabled } from 'contexts/serviceContext';
import {
  DrumkitPlatform,
  SidebarStateContext,
} from 'contexts/sidebarStateContext';
import useLogPostHogPageView from 'hooks/useLogPostHogPageView';
import { useQQAgentInvocations } from 'hooks/useQQAgentInvocations';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { getCarouselSuggestionStabilityKey } from 'pages/QuoteView/LoadBuilding/sharepoint/utils/sharepointSuggestions';
import { Email } from 'types/Email';
import { Maybe, Undef } from 'types/UtilityTypes';
import { LaneReasoning } from 'types/agents/QuickQuoteAgent';
import Pageview from 'types/enums/Pageview';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';
import { isQuoteRequestActionable } from 'utils/isQuoteRequestActionable';
import {
  resolveDefaultProfit,
  resolveDefaultProfitType,
} from 'utils/quickQuoteDefaults';

import QuickQuoteAgents from './Agent/QuickQuoteAgents';
import {
  findSuggestionForLane,
  isWrittenRulesInvocation,
} from './Agent/writtenRules/reasoningHelpers';
import { BatchLTLQuoteForm } from './BatchLTL/BatchLTLQuoteForm';
import { findBatchLTLState } from './BatchLTL/utils/batchLTLStorage';
import {
  getBatchLTLSuggestions,
  isBatchLTLQuoteAvailable,
} from './BatchLTL/utils/getBatchLTLSuggestions';
import { getRestorableBatchSuggestions } from './BatchLTL/utils/restoreBatchLTLState';
import BatchQuoteForm from './BatchQuote/BatchQuoteForm';
import QuickQuoteForm from './QuickQuoteForm';
import { QuickQuoteProvider } from './context/QuickQuoteContext';
import { useIsOnQuotingPortal } from './hooks';
import { QuoteMode } from './types';

type QuickQuoteTabProps = {
  email: Maybe<Email>;
};

enum QuickQuoteSections {
  QuickQuote = 'quick-quote',
  BatchQuote = 'batch-quote',
  BatchLTLQuote = 'batch-ltl-quote',
  Agents = 'agents',
}

type QuickQuoteSectionsArray = Undef<QuickQuoteSections[]>;

type BatchLTLSnapshot = {
  emailId: Maybe<number>;
  suggestions: SuggestedQuoteChange[];
};

const NO_SUGGESTIONS: SuggestedQuoteChange[] = [];

export default function QuickQuoteTab({ email }: QuickQuoteTabProps) {
  const {
    serviceID,
    serviceFeaturesEnabled,
    tmsIntegrations,
    ltlTMSIntegrations,
    configurations: {
      defaultPriceMargin: userDefaultMargin,
      defaultPriceMarginType: userDefaultMarginType,
    },
    quickQuoteConfig,
  } = useServiceFeatures();
  const {
    currentState: {
      curSuggestionList,
      drumkitPlatform,
      batchSelectedSuggestionIds,
      clickedSuggestion,
      inboxEmailAddress,
    },
    setCurrentState,
  } = useContext(SidebarStateContext);
  const quickQuoteSectionRef = useRef<HTMLDivElement>(null);

  // Hide Batch Quote on bidding portals (ENG-8031); draft hide uses the same hook in QuickQuoteForm.
  const isOnQuotingPortal = useIsOnQuotingPortal();

  const pendingQuickQuoteSuggestions = useMemo(
    () =>
      curSuggestionList.filter(
        (s) =>
          s.pipeline === SuggestionPipelines.QuickQuote &&
          isQuoteRequestActionable(s)
      ),
    [curSuggestionList]
  );

  const isBatchLTLEnabled =
    !isOnQuotingPortal &&
    isBatchLTLQuoteAvailable({
      isLTLQuickQuoteEnabled: serviceFeaturesEnabled.isLTLQuickQuoteEnabled,
      ltlTMSIntegrations,
    });

  // Two or more LTL shipments from one email get their own panel instead of FTL Batch Quote.
  const pendingBatchLTLSuggestions = useMemo(
    () =>
      isBatchLTLEnabled
        ? getBatchLTLSuggestions(pendingQuickQuoteSuggestions, email?.id)
        : NO_SUGGESTIONS,
    [isBatchLTLEnabled, pendingQuickQuoteSuggestions, email?.id]
  );

  // A batch the rep worked on before leaving the email, read from the browser.
  const threadIdForStorage = email?.threadID ?? '';
  const candidateEmailIdsKey = [
    email?.id ?? 0,
    ...pendingQuickQuoteSuggestions.map((s) => s.emailID),
  ].join(',');
  const storedBatchLTL = useMemo(
    () =>
      isBatchLTLEnabled && threadIdForStorage
        ? findBatchLTLState(
            serviceID,
            threadIdForStorage,
            candidateEmailIdsKey.split(',').map(Number)
          )
        : null,
    [isBatchLTLEnabled, serviceID, threadIdForStorage, candidateEmailIdsKey]
  );
  const restorableBatchLTLSuggestions = useMemo(
    () => getRestorableBatchSuggestions(storedBatchLTL, curSuggestionList),
    [storedBatchLTL, curSuggestionList]
  );

  // The saved batch wins while the pending shipments are all part of it, so shipments
  // already in the rep's reply stay on screen; new shipments start a new batch.
  const isPendingBatchSaved = pendingBatchLTLSuggestions.every((pending) =>
    restorableBatchLTLSuggestions.some((saved) => saved.id === pending.id)
  );
  const batchLTLSuggestions =
    restorableBatchLTLSuggestions.length > 0 && isPendingBatchSaved
      ? restorableBatchLTLSuggestions
      : pendingBatchLTLSuggestions;

  // Drafting the reply completes the quoted shipments in Kit, which drops them from the
  // pending list. Keep showing the email's batch so the rep sees the confirmation and can
  // retry what failed; only new shipments or another email replace it.
  const emailId = email?.id ?? null;
  const [batchLTLSnapshot, setBatchLTLSnapshot] = useState<BatchLTLSnapshot>({
    emailId: null,
    suggestions: [],
  });
  const hasNewBatchLTLSuggestions =
    batchLTLSuggestions.length > 0 &&
    (batchLTLSnapshot.emailId !== emailId ||
      batchLTLSuggestions.some(
        (s) => !batchLTLSnapshot.suggestions.some((shown) => shown.id === s.id)
      ));
  useEffect(() => {
    if (hasNewBatchLTLSuggestions) {
      setBatchLTLSnapshot({ emailId, suggestions: batchLTLSuggestions });
    }
  }, [batchLTLSuggestions, emailId, hasNewBatchLTLSuggestions]);
  let shownBatchLTLSuggestions = NO_SUGGESTIONS;
  if (hasNewBatchLTLSuggestions) {
    shownBatchLTLSuggestions = batchLTLSuggestions;
  } else if (batchLTLSnapshot.emailId === emailId) {
    shownBatchLTLSuggestions = batchLTLSnapshot.suggestions;
  }

  const showBatchLTLSection = shownBatchLTLSuggestions.length > 0;
  // "Start over" in the panel remounts it with fresh defaults.
  const [batchLTLResetCount, setBatchLTLResetCount] = useState(0);
  const ftlBatchSuggestions = useMemo(
    () =>
      pendingQuickQuoteSuggestions.filter(
        (s) => !shownBatchLTLSuggestions.some((ltl) => ltl.id === s.id)
      ),
    [pendingQuickQuoteSuggestions, shownBatchLTLSuggestions]
  );
  const batchQuoteFormSuggestions = useMemo(() => {
    const selectedFtlSuggestionIds = batchSelectedSuggestionIds.filter((id) =>
      ftlBatchSuggestions.some((suggestion) => suggestion.id === id)
    );

    return selectedFtlSuggestionIds.length > 0
      ? ftlBatchSuggestions.filter((suggestion) =>
          selectedFtlSuggestionIds.includes(suggestion.id)
        )
      : ftlBatchSuggestions;
  }, [batchSelectedSuggestionIds, ftlBatchSuggestions]);
  const showBatchQuoteSection =
    serviceFeaturesEnabled.isBatchQuoteEnabled &&
    !isOnQuotingPortal &&
    !(showBatchLTLSection && ftlBatchSuggestions.length === 0);

  const pendingQuickQuoteCount = pendingQuickQuoteSuggestions.length;

  const [accordionTab, setAccordionTab] =
    useState<QuickQuoteSectionsArray>(undefined);

  const {
    invocations,
    quickQuotesByInvocationId,
    ruleSetsByInvocationId,
    loading,
    error: fetchAgentInvocationsError,
    showAgentSection,
  } = useQQAgentInvocations({
    threadId: email?.threadID || null,
    enabled: serviceFeaturesEnabled.isQuickQuoteAgentEnabled,
    showFailedLLMInvocations: isWrittenRulesAgentEnabled(
      serviceFeaturesEnabled
    ),
  });

  const batchQuoteSectionRef = useRef<HTMLDivElement>(null);

  // Scrolling waits a beat for the accordion to open; cancel it if the tab unmounts first.
  const scrollTimeoutRef = useRef<Maybe<number>>(null);
  useEffect(
    () => () => {
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    },
    []
  );
  const scrollToSectionSoon = useCallback((scroll: () => void) => {
    if (scrollTimeoutRef.current !== null) {
      window.clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = window.setTimeout(scroll, 150);
  }, []);

  const batchSelectedCount = batchSelectedSuggestionIds.length;
  const isQuickQuoteClicked =
    clickedSuggestion?.pipeline === SuggestionPipelines.QuickQuote;

  // Sections opened by default for this email; derived from counts so a rep's own
  // open/close survives re-renders that don't change them.
  const defaultTabs = useMemo(() => {
    const baseTabs = [];
    // Kit clears batchSelectedSuggestionIds when the user picks a single lane
    // while multiple QQ suggestions remain pending.
    const isSingleLaneFromBatch =
      batchSelectedCount === 0 &&
      pendingQuickQuoteCount > 1 &&
      isQuickQuoteClicked;

    if (showBatchLTLSection) {
      // Batch LTL stays open after its reply, even with one shipment left.
      baseTabs.push(QuickQuoteSections.BatchLTLQuote);
      if (isSingleLaneFromBatch) {
        baseTabs.push(QuickQuoteSections.QuickQuote);
      }
    } else if (pendingQuickQuoteCount === 1) {
      baseTabs.push(QuickQuoteSections.QuickQuote);
    } else if (
      pendingQuickQuoteCount > 1 &&
      showBatchQuoteSection &&
      !isSingleLaneFromBatch
    ) {
      baseTabs.push(QuickQuoteSections.BatchQuote);
    } else {
      // 0 pending, or multi-pending narrowed to one lane: QQ open, Batch collapsed
      baseTabs.push(QuickQuoteSections.QuickQuote);
    }

    // Only add Agents tab if there are invocations to show
    if (showAgentSection) {
      baseTabs.push(QuickQuoteSections.Agents);
    }

    return baseTabs;
  }, [
    pendingQuickQuoteCount,
    batchSelectedCount,
    isQuickQuoteClicked,
    showBatchQuoteSection,
    showBatchLTLSection,
    showAgentSection,
  ]);

  // Reopen the default sections when they change or the rep opens another email.
  useEffect(() => {
    setAccordionTab(defaultTabs);
  }, [defaultTabs, emailId]);

  const hasQRSuggestions = pendingQuickQuoteCount > 0;

  const isWrittenRulesAgentShown =
    isWrittenRulesAgentEnabled(serviceFeaturesEnabled) &&
    invocations.some(isWrittenRulesInvocation);

  // "Open in Quick Quote" / "Quote anyway" from the written-rules card: load the lane's
  // extracted request into the Quick Quote form and bring it into view.
  const handleOpenInQuickQuote = useCallback(
    (lane?: LaneReasoning) => {
      const suggestion = findSuggestionForLane(
        pendingQuickQuoteSuggestions as SuggestedQuoteChange[],
        lane
      );
      if (suggestion) {
        setCurrentState((prevState) => ({
          ...prevState,
          clickedSuggestion: suggestion,
          batchSelectedSuggestionIds: [],
          shouldKitAutoSelect: true,
        }));
      }

      // Selecting one lane (and clearing the batch selection) lets the tab effect open Quick
      // Quote as it does for a Kit single-lane pick; this also opens it if the rep had closed it.
      setAccordionTab((prevTabs) =>
        prevTabs?.includes(QuickQuoteSections.QuickQuote)
          ? prevTabs
          : [...(prevTabs ?? []), QuickQuoteSections.QuickQuote]
      );

      scrollToSectionSoon(() => {
        quickQuoteSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    },
    [pendingQuickQuoteSuggestions, setCurrentState, scrollToSectionSoon]
  );

  // "Open in Quick Quote" on a Batch LTL shipment: rate that one in the single LTL form
  // while the batch panel stays open with the rest.
  const handleOpenBatchLTLShipmentInQuickQuote = useCallback(
    (quoteRequestId: number) => {
      const suggestion = shownBatchLTLSuggestions.find(
        (s) => s.id === quoteRequestId
      );
      if (!suggestion) {
        return;
      }

      setCurrentState((prevState) => ({
        ...prevState,
        clickedSuggestion: suggestion,
        batchSelectedSuggestionIds: [],
      }));
      setAccordionTab((prevTabs) =>
        prevTabs?.includes(QuickQuoteSections.QuickQuote)
          ? prevTabs
          : [...(prevTabs ?? []), QuickQuoteSections.QuickQuote]
      );
      scrollToSectionSoon(() => {
        quickQuoteSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    },
    [shownBatchLTLSuggestions, setCurrentState, scrollToSectionSoon]
  );

  // "Open Batch Quote" from a written-rules batch card: select every lane for Batch Quote (the
  // tab effect then opens it, as for Kit's batch pick) and move the rep there.
  const handleOpenBatchQuote = useCallback(() => {
    const pendingIds = pendingQuickQuoteSuggestions.map((s) => s.id);
    setCurrentState((prevState) => ({
      ...prevState,
      clickedSuggestion: pendingQuickQuoteSuggestions[0] ?? null,
      batchSelectedSuggestionIds: pendingIds,
      shouldKitAutoSelect: true,
    }));
    setAccordionTab((prevTabs) =>
      prevTabs?.includes(QuickQuoteSections.BatchQuote)
        ? prevTabs
        : [...(prevTabs ?? []), QuickQuoteSections.BatchQuote]
    );

    scrollToSectionSoon(() => {
      const section = batchQuoteSectionRef.current;
      section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      section?.querySelector<HTMLButtonElement>('button')?.focus({
        preventScroll: true,
      });
    });
  }, [pendingQuickQuoteSuggestions, setCurrentState, scrollToSectionSoon]);

  useLogPostHogPageView(
    Pageview.QuickQuote,
    {
      serviceID,
      emailID: email?.id,
      hasQRSuggestions,
    },
    drumkitPlatform === DrumkitPlatform.Outlook
      ? ['serviceID', 'emailID']
      : ['serviceID']
  );

  const getDefaultProfit = useCallback(() => {
    return resolveDefaultProfit({
      inboxEmailAddress,
      userDefaultMargin,
      userDefaultMarginType,
      serviceDefaultMarginType: quickQuoteConfig?.defaultMarginType,
      serviceDefaultFlatMargin: quickQuoteConfig?.defaultFlatMargin,
      serviceDefaultPercentMargin: quickQuoteConfig?.defaultPercentMargin,
    });
  }, [
    inboxEmailAddress,
    userDefaultMargin,
    userDefaultMarginType,
    quickQuoteConfig,
  ]);

  const getDefaultProfitType = useCallback(() => {
    return resolveDefaultProfitType({
      inboxEmailAddress,
      userDefaultMarginType,
      serviceDefaultMarginType: quickQuoteConfig?.defaultMarginType,
    });
  }, [inboxEmailAddress, userDefaultMarginType, quickQuoteConfig]);

  // Remount Batch Quote when the email, load identity, or selected lane set changes.
  // The selection key lets Kit's "Apply all lanes" add pending lanes to an already
  // open form without relying on react-hook-form to adopt new default values.
  const suggestionIdentifiers = ftlBatchSuggestions
    .map(getCarouselSuggestionStabilityKey)
    .join(':');
  const batchSelectionKey = [...batchSelectedSuggestionIds]
    .sort((a, b) => a - b)
    .join(':');
  const batchQuoteFormKey = `${email?.id ?? 'no-email'}:${suggestionIdentifiers}:${batchSelectionKey}`;
  const batchLTLFormKey = `${email?.id ?? 'no-email'}:${batchLTLResetCount}:${shownBatchLTLSuggestions
    .map((s) => s.id)
    .join(':')}`;

  return (
    <div className='mb-4'>
      {accordionTab && (
        <Accordion
          type='multiple'
          value={accordionTab}
          onValueChange={(v) => setAccordionTab(v as QuickQuoteSections[])}
        >
          {showAgentSection && (
            <AccordionItem value={QuickQuoteSections.Agents}>
              <AccordionTrigger
                icon={<Bot className='h-5 w-5' strokeWidth={2} />}
                underline={false}
                className='group'
              >
                <Flex align='center' gap='sm'>
                  <Typography variant='h5' className='group-hover:underline'>
                    {isWrittenRulesAgentShown ? 'Kit Agent' : 'Agents'}
                  </Typography>
                  {!isWrittenRulesAgentShown && (
                    <Typography
                      variant='body-xxs'
                      className='text-neutral-50 bg-brand-main/90 rounded-full px-2 py-0.5'
                      as='span'
                    >
                      Beta
                    </Typography>
                  )}
                </Flex>
              </AccordionTrigger>
              <AccordionContent>
                <QuickQuoteAgents
                  invocations={invocations}
                  quickQuotesByInvocationId={quickQuotesByInvocationId}
                  ruleSetsByInvocationId={ruleSetsByInvocationId}
                  onOpenInQuickQuote={handleOpenInQuickQuote}
                  onOpenBatchQuote={
                    showBatchQuoteSection && pendingQuickQuoteCount > 1
                      ? handleOpenBatchQuote
                      : undefined
                  }
                  loading={loading}
                  error={fetchAgentInvocationsError}
                />
              </AccordionContent>
            </AccordionItem>
          )}

          <AccordionItem
            value={QuickQuoteSections.QuickQuote}
            ref={quickQuoteSectionRef}
            className='scroll-mt-2'
          >
            <AccordionTrigger
              icon={<DollarSign className='h-5 w-5 group' strokeWidth={2} />}
            >
              <Typography variant='h5' className='group-hover:underline'>
                Quick Quote
              </Typography>
            </AccordionTrigger>
            <AccordionContent>
              <ErrorBoundary>
                <QuickQuoteProvider
                  tmsIntegrations={tmsIntegrations}
                  ltlTMSIntegrations={ltlTMSIntegrations}
                  defaultProfitType={getDefaultProfitType()}
                  defaultProfit={getDefaultProfit()}
                >
                  <QuickQuoteForm email={email} />
                </QuickQuoteProvider>
              </ErrorBoundary>
            </AccordionContent>
          </AccordionItem>

          {showBatchQuoteSection && (
            <AccordionItem
              value={QuickQuoteSections.BatchQuote}
              ref={batchQuoteSectionRef}
              className='scroll-mt-2'
            >
              <AccordionTrigger
                icon={<List className='h-5 w-5' strokeWidth={2} />}
                underline={false}
                className='group'
              >
                <Flex align='center' gap='sm'>
                  <Typography variant='h5' className='group-hover:underline'>
                    Batch Quote
                  </Typography>
                  <Typography
                    variant='body-xxs'
                    className='text-neutral-50 bg-brand-main/90 rounded-full px-2 py-0.5'
                    as='span'
                  >
                    Beta
                  </Typography>
                </Flex>
              </AccordionTrigger>
              <AccordionContent>
                <ErrorBoundary>
                  <BatchQuoteForm
                    key={batchQuoteFormKey}
                    suggestions={batchQuoteFormSuggestions}
                    email={email}
                  />
                </ErrorBoundary>
              </AccordionContent>
            </AccordionItem>
          )}

          {showBatchLTLSection && (
            <AccordionItem
              value={QuickQuoteSections.BatchLTLQuote}
              className='scroll-mt-2'
            >
              <AccordionTrigger
                icon={<Package className='h-5 w-5' strokeWidth={2} />}
                underline={false}
                className='group'
              >
                <Flex align='center' gap='sm'>
                  <Typography variant='h5' className='group-hover:underline'>
                    Batch LTL Quote
                  </Typography>
                  <Typography
                    variant='body-xxs'
                    className='text-neutral-50 bg-brand-main/90 rounded-full px-2 py-0.5'
                    as='span'
                  >
                    Beta
                  </Typography>
                </Flex>
              </AccordionTrigger>
              <AccordionContent>
                <ErrorBoundary>
                  <QuickQuoteProvider
                    tmsIntegrations={tmsIntegrations}
                    ltlTMSIntegrations={ltlTMSIntegrations}
                    defaultQuoteMode={QuoteMode.LTL}
                  >
                    <BatchLTLQuoteForm
                      key={batchLTLFormKey}
                      suggestions={shownBatchLTLSuggestions}
                      onOpenInQuickQuote={
                        handleOpenBatchLTLShipmentInQuickQuote
                      }
                      onStartOver={() =>
                        setBatchLTLResetCount((count) => count + 1)
                      }
                      email={email}
                    />
                  </QuickQuoteProvider>
                </ErrorBoundary>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      )}
    </div>
  );
}
